import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
export function FooterCTA() {
  const containerRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.8, 1, 1, 0.8]);
  return <section ref={containerRef} className="relative h-[80vh] min-h-[600px] overflow-hidden" data-id="element-106">
      {/* Background Image with Parallax */}
      <motion.div style={{
      y
    }} className="absolute inset-0 w-full h-[120%] -top-[10%]" data-id="element-107">
        <img src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80" alt="Mountain home construction site overlooking Idaho landscape" className="w-full h-full object-cover" data-id="element-108" />

        {/* Warm overlay - inviting, not dark */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/85 via-[#1A1A1A]/70 to-[#1A1A1A]/50" data-id="element-109" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 via-transparent to-[#1A1A1A]/40" data-id="element-110" />

        {/* Warm color tint */}
        <div className="absolute inset-0 bg-[#A07656]/10 mix-blend-overlay" data-id="element-111" />
      </motion.div>

      {/* Content */}
      <motion.div style={{
      opacity
    }} className="relative z-10 h-full flex items-center" data-id="element-112">
        <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 w-full" data-id="element-113">
          <div className="max-w-2xl" data-id="element-114">
            {/* Eyebrow */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="flex items-center gap-3 mb-8" data-id="element-115">
              <div className="w-10 h-10 border-2 border-[#A07656]/50 flex items-center justify-center" data-id="element-116">
                <MapPin className="w-5 h-5 text-[#A07656]" data-id="element-117" />
              </div>
              <span className="text-[#A07656] uppercase tracking-[0.3em] text-xs font-semibold" data-id="element-118">
                Start Here
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h2 initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-[#F5F3F0] leading-[1.1] mb-6" data-id="element-119">
              Let's Walk
              <br data-id="element-120" />
              <span className="italic font-semibold text-[#D4CFC8]" data-id="element-121">
                Your Land.
              </span>
            </motion.h2>

            {/* Subtext */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-lg md:text-xl text-[#E8E4DF]/70 leading-relaxed mb-10 max-w-lg" data-id="element-122">
              The best homes start with a conversation on the build site. Let us
              meet you there to discuss your vision.
            </motion.p>

            {/* CTA Button */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} data-id="element-123">
              <motion.a href="https://calendar.app.google/5bvWP7BUZxFVtkSy8" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} className="group relative inline-flex items-center gap-4 px-10 py-5 bg-[#A07656] text-white overflow-hidden transition-all duration-300 hover:bg-[#B8896A] hover:shadow-[0_0_50px_rgba(160,118,86,0.4)]" data-id="element-124">
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-125" />

                <span className="relative z-10 text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-126">
                  Schedule a Site Visit
                </span>
                <ArrowRight className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" data-id="element-127" />
              </motion.a>

              {/* Secondary link */}
              <motion.p initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5
            }} className="mt-6 text-sm text-[#D4CFC8]/50" data-id="element-128">
                Or call us directly:{' '}
                <a href="tel:+12087550488" className="text-[#A07656] hover:text-[#B8896A] transition-colors font-medium" data-id="element-129">
                  (208) 755-0488
                </a>
              </motion.p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#A07656]/30 to-transparent" data-id="element-130" />

      {/* Corner accent */}
      <div className="absolute top-12 right-12 hidden lg:block" data-id="element-131">
        <div className="w-24 h-24 border-t-2 border-r-2 border-[#A07656]/20" data-id="element-132" />
      </div>
    </section>;
}